/**
 * SmartAnomalyBanner — top-of-dashboard predictive banner. Detects
 * simple anomalies from the landlord's own data (no LLM cost) and
 * surfaces them with a Smart accent.
 *
 * Detected rules (all deterministic, all client-side):
 *   • Rent-collection spike/drop >= 25% vs last month
 *   • Any lease expiring in < 14 days without a renewal offer sent
 *   • Any maintenance ticket open > 7 days
 *   • Any tenant with rent > 5 days late
 *
 * Prop:
 *   metrics — object shape:
 *     {
 *       rent_this_month, rent_last_month,
 *       leases_expiring_14d, open_tickets_7d, tenants_5d_late,
 *     }
 * Silent when there are no anomalies (no false alarms).
 */
import { Sparkles, TrendingDown, TrendingUp, AlertTriangle, Clock, X } from "lucide-react";
import { useState } from "react";
import "./SmartAccent.css";

function detect(m) {
  if (!m) return [];
  const out = [];
  if (m.rent_this_month != null && m.rent_last_month) {
    const delta = m.rent_this_month - m.rent_last_month;
    const pct = m.rent_last_month ? (delta / m.rent_last_month) * 100 : 0;
    if (pct <= -25) out.push({
      kind: "rent-drop", icon: TrendingDown,
      msg: `Rent collected this month is ${Math.abs(Math.round(pct))}% below last month — Copilot suggests you review the late-rent queue.`,
      href: "/rent?filter=late",
    });
    if (pct >= 25) out.push({
      kind: "rent-spike", icon: TrendingUp,
      msg: `Rent collection is up ${Math.round(pct)}% vs last month — great time to schedule a lease-renewal push.`,
      href: "/renewals",
    });
  }
  if (m.leases_expiring_14d > 0) out.push({
    kind: "lease-expiring", icon: Clock,
    msg: `${m.leases_expiring_14d} lease${m.leases_expiring_14d === 1 ? "" : "s"} expiring in the next 14 days — Copilot can draft renewal offers now.`,
    href: "/renewals",
  });
  if (m.open_tickets_7d > 0) out.push({
    kind: "tickets-stale", icon: AlertTriangle,
    msg: `${m.open_tickets_7d} maintenance ticket${m.open_tickets_7d === 1 ? "" : "s"} open longer than 7 days — Copilot can auto-nudge the vendor.`,
    href: "/maintenance",
  });
  if (m.tenants_5d_late > 0) out.push({
    kind: "rent-late", icon: AlertTriangle,
    msg: `${m.tenants_5d_late} tenant${m.tenants_5d_late === 1 ? " is" : "s are"} 5+ days behind on rent — Copilot can generate a friendly reminder.`,
    href: "/rent?filter=late",
  });
  return out;
}

export default function SmartAnomalyBanner({ metrics, onNavigate }) {
  const anomalies = detect(metrics);
  const [dismissedIdx, setDismissedIdx] = useState(new Set());
  const visible = anomalies.filter((_, i) => !dismissedIdx.has(i));
  if (visible.length === 0) return null;
  const a = visible[0]; // one at a time — never stack banners
  const Icon = a.icon;

  return (
    <div
      data-testid="smart-anomaly-banner"
      className="relative mb-4 rounded-xl border smart-accent-ring bg-white p-3 sm:p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-emerald-100 via-sky-100 to-violet-100 flex-none">
          <Sparkles className="w-4 h-4 text-violet-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-700 inline-flex items-center gap-1 mb-1">
            <Icon className="w-3 h-3" /> Copilot noticed
          </div>
          <p className="text-sm text-slate-800 leading-relaxed">{a.msg}</p>
          {a.href && (
            <button
              onClick={() => onNavigate ? onNavigate(a.href) : (window.location.href = a.href)}
              data-testid={`smart-anomaly-cta-${a.kind}`}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full smart-accent-btn text-xs font-bold"
            >
              <Sparkles className="w-3 h-3" /> Take me there
            </button>
          )}
        </div>
        <button
          onClick={() => setDismissedIdx((s) => new Set(s).add(anomalies.indexOf(a)))}
          data-testid="smart-anomaly-dismiss"
          aria-label="Dismiss"
          className="text-slate-400 hover:text-slate-600 p-1 flex-none"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
